import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AiChatSessionService } from "../../../../services/aiChatSessionService";
import IGlobalConfig from "../../../../types/IGlobalConfig";
import { AiChatSessionSummary } from "../../../../types/services/IAiChatSession";

function chatErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }
  const message = error.message.trim();
  if (message.includes("NOT_CHARITY_USER")) {
    return message.replace(/^NOT_CHARITY_USER\s*\/\s*/i, "").trim() || fallback;
  }
  return message || fallback;
}

interface UseChatSessionListParams {
  config: IGlobalConfig;
  serviceAccessToken: string;
  charityId?: string;
}

interface UseChatSessionListReturn {
  sessions: AiChatSessionSummary[];
  loading: boolean;
  error: string | null;
  reloadSessions: () => Promise<void>;
  handleSessionSelect: (id: string) => void;
}

export function useChatSessionList({
  config,
  serviceAccessToken,
  charityId,
}: UseChatSessionListParams): UseChatSessionListReturn {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<AiChatSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reloadSessions = useCallback(async () => {
    if (!charityId) {
      setSessions([]);
      setError("Select a charity to load conversations.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await AiChatSessionService.listSessions(
        config,
        serviceAccessToken,
        charityId
      );
      setSessions(result.sessions);
    } catch (err) {
      setError(chatErrorMessage(err, "Failed to load conversations."));
    } finally {
      setLoading(false);
    }
  }, [charityId, config, serviceAccessToken]);

  useEffect(() => {
    reloadSessions();
  }, [reloadSessions]);

  const handleSessionSelect = useCallback(
    (id: string) => {
      navigate(`/chat-session/${id}`);
    },
    [navigate]
  );

  return {
    sessions,
    loading,
    error,
    reloadSessions,
    handleSessionSelect,
  };
}
